import React, { memo, RefObject, useRef } from 'react';
import { Button, Image, Link, Text } from '~/shared/components';
import { Carousel } from '~/shared/components/Carousel';
import Flex from '~/shared/components/Flex';
import { M21ReferencedCategoriesModule, PageReference } from '~/lib/data-contract';
import { weakKey } from '~/shared/utils/jsx';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import { useTheme } from '@emotion/react';
import ChevronLeft from '~/icons/chevron-left.svg';
import ChevronRight from '~/icons/chevron.svg';
import { breakpointsRaw, queries } from '~/theme/breakpoints';
import styles from './M21ReferencedCategories.module.scss';
import { ModuleHeader } from '../ModuleHeader';

export type M21ReferencedCategoriesProps = M21ReferencedCategoriesModule;

/**
 * List of page references, extracts data from references field in CMS.
 * The layout is supposed to be used with presentational images and text which describes the page.
 */
export const M21ReferencedCategories = memo(function M21ReferencedCategories({
    items,
    headline,
    subHeadline,
    callToAction,
    priority,
    textColor,
    isLastItem,
    ...rest
}: M21ReferencedCategoriesProps) {
    const leftArrow = useRef<HTMLButtonElement>(null);
    const rightArrow = useRef<HTMLButtonElement>(null);

    return (
        <>
            {!!items?.length && (
                <ModuleContainer className={styles.container} {...rest}>
                    <Flex
                        alignItems="start"
                        justifyContent="space-between"
                        css={{ color: textColor }}
                    >
                        <Button className={styles.button} variant="icon" ref={leftArrow}>
                            <ChevronLeft />
                        </Button>

                        <ModuleHeader
                            noGutter
                            headline={headline}
                            subHeadline={subHeadline}
                            link={callToAction}
                            textColor={textColor}
                        />

                        <Button className={styles.button} variant="icon" ref={rightArrow}>
                            <ChevronRight />
                        </Button>
                    </Flex>

                    <CategoryCarousel
                        isSkipHidden={isLastItem}
                        leftArrow={leftArrow}
                        rightArrow={rightArrow}
                        priority={priority}
                        items={items}
                        textColor={textColor}
                    />
                </ModuleContainer>
            )}
        </>
    );
});

// In case we need the "list" version of the component, we can use this one:
// const List = (props: { items: PageReference[] }) => {
//     const { items } = props;
//     const { sizes } = useTheme();
//     const columnCount = Math.min(items?.length || 0, 5);
//     const imagesVW = 100 / columnCount;
//     const imagesMaxWidth = sizes.contentMaxWidth / columnCount;

//     return (
//         <StyledCategories>
//             {items?.map((item) => (
//                 <Card
//                     key={weakKey(item)}
//                     item={item}
//                     imagesVW={imagesVW}
//                     imagesMaxWidth={imagesMaxWidth}
//                 />
//             ))}
//         </StyledCategories>
//     );
// };

type CarouselProps = {
    items: PageReference[];
    priority?: boolean;
    leftArrow?: RefObject<HTMLButtonElement>;
    rightArrow?: RefObject<HTMLButtonElement>;
    textColor?: string;
    isSkipHidden?: boolean;
};

function CategoryCarousel(props: CarouselProps) {
    const { items, priority, leftArrow, rightArrow, textColor, isSkipHidden } = props;
    const { sizes } = useTheme();
    const columnCount = Math.min(items?.length || 0, 4);
    const imagesVW = 100 / columnCount;
    const imagesMaxWidth = sizes.pageMaxWidth / columnCount;
    return (
        <Carousel
            items={items?.map((item) => (
                <Card
                    key={weakKey(item)}
                    item={item}
                    priority={priority}
                    imagesVW={imagesVW}
                    textColor={textColor}
                    imagesMaxWidth={imagesMaxWidth}
                />
            ))}
            options={{
                slidesPerView: 1.3,
                breakpoints: {
                    [breakpointsRaw.md]: {
                        slidesPerView: 3.8,
                    },
                },
            }}
            prevRef={leftArrow}
            nextRef={rightArrow}
            isSkipHidden={isSkipHidden}
        ></Carousel>
    );
}

function Card(props: {
    item: PageReference;
    imagesVW: number;
    imagesMaxWidth: number;
    priority?: boolean;
    textColor?: string;
}): JSX.Element {
    const { item, imagesVW, imagesMaxWidth, priority, textColor } = props;
    return (
        <div className={styles.categoryContainer}>
            <Link
                href={item.callToAction?.url}
                target={item.callToAction?.target}
                title={item.callToAction?.title}
                noHover
            >
                {item.pageImage?.src ? (
                    <div className={styles.imageContainer}>
                        <Image
                            src={item.pageImage.src}
                            alt={item.pageImage.name}
                            priority={priority}
                            layout="responsive"
                            height={456}
                            width={367}
                            objectPosition="center"
                            objectFit="cover"
                            sizes={`${queries.xs} 100vw, ${queries.sm} 50vw, ${queries.contentMax} ${imagesVW}vw,  ${imagesMaxWidth}px`}
                            focalPoint={item.pageImage.focalPoint}
                        />
                    </div>
                ) : null}
                <div>
                    <Text variant="bodySmall">{item.parentName}</Text>
                    <Text className={styles.headline} color={textColor} variant="display3">
                        {item.pageTitle}
                    </Text>
                    <Text className={styles.subHeadline} color={textColor} variant="body">
                        {item.pageDescription}
                    </Text>
                </div>
            </Link>
        </div>
    );
}
